<template>
  <div class="edit-bill-template page--edit-bill-template">
    <div class="page__header">
      <div class="page__title">
        <router-link :to="backLinkRoute" class="page__back-link">
          <arrow-button
            class="page__back-link-icon"
            :rotated180="true"
          ></arrow-button>
        </router-link>
        <div class="page__title-text">
          {{ $t('edit-bill-template.page.title') }}
        </div>
      </div>
    </div>
    <div class="edit-bill-template__form">
      <div class="edit-bill-template__form-fields">
        <div class="edit-bill-template__form-field">
          <text-input
            v-model:value="templateData.templateName"
            :title="$t('edit-bill-template.fields.template-name')"
            :placeholder="
              $t('edit-bill-template.fields.template-name-placeholder')
            "
          ></text-input>
        </div>
        <div
          class="edit-bill-template__form-field edit-bill-template__form-field--row"
        >
          <custom-select
            class="edit-bill-template__form-field-small"
            v-model:value="templateData.currency"
            mode="big"
            value-key="id"
            :title="$t('edit-bill-template.fields.currency')"
            :options="currencyOptions"
          ></custom-select>
          <text-input
            v-model:value="templateData.sum"
            :mask="numberMask"
            :placeholder="$t('edit-bill-template.fields.sum-placeholder')"
            :title="$t('edit-bill-template.fields.sum')"
          ></text-input>
        </div>
        <div class="edit-bill-template__form-field">
          <text-input
            v-model:value="templateData.purpose"
            :title="$t('edit-bill-template.fields.purpose')"
            :placeholder="$t('edit-bill-template.fields.purpose-placeholder')"
          ></text-input>
        </div>
        <div class="edit-bill-template__form-field">
          <custom-date-picker
            v-model:value="templateData.expAt"
            :disabled-date-handler="disabledDateHandler"
            :label="$t('edit-bill-template.fields.exp-at')"
            mode="big"
          ></custom-date-picker>
        </div>
        <div class="edit-bill-template__form-field">
          <text-input
            v-model:value="templateData.comment"
            :hint="$t('edit-bill-template.fields.comment-hint')"
            :title="$t('edit-bill-template.fields.comment')"
            :placeholder="$t('edit-bill-template.fields.comment-placeholder')"
          ></text-input>
        </div>
      </div>
      <div class="edit-bill-template__form-actions">
        <primary-button
          class="edit-bill-template__form-action"
          :disabled="billTemplateSaving || !isFormValid"
          :label="$t('edit-bill-template.actions.save')"
          @click="saveBillTemplate"
        ></primary-button>
      </div>
    </div>
    <success-notification
      ref="billTemplateSavedNotification"
      :text="$t('edit-bill-template.notifications.saved')"
    ></success-notification>
  </div>
</template>

<script>
import ArrowButton from '@vue-app/components/base/button/ArrowButton.vue'
import TextInput from '@vue-app/components/base/input/TextInput.vue'
import PrimaryButton from '@vue-app/components/base/button/PrimaryButton.vue'
import CustomSelect from '@vue-app/components/base/input/CustomSelect.vue'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import CustomDatePicker from '@vue-app/components/base/input/CustomDatePicker.vue'
import moment from 'moment'
import CheckboxInput from '@vue-app/components/base/input/CheckboxInput.vue'
import { RouteHelper } from '@js-app/services/helpers/routes/routes-helper'
import { CompaniesApi } from '@js-app/office/api/companies-api'
import SuccessNotification from '@vue-app/components/base/popup/notification/SuccessNotification.vue'

const numberMask = createNumberMask({
  prefix: '',
  allowDecimal: true,
  includeThousandsSeparator: false,
  allowNegative: false,
  decimalLimit: 2,
})

export default {
  name: 'CreateBill',
  props: ['id', 'templateId'],
  components: {
    SuccessNotification,
    CheckboxInput,
    CustomDatePicker,
    CustomSelect,
    PrimaryButton,
    TextInput,
    ArrowButton,
  },
  data() {
    return {
      templateData: {
        sum: '',
        purpose: '',
        expAt: null,
        templateName: '',
        currency: 'uah',
        comment: '',
      },
      langOptions: [
        { id: 'uk', name: 'edit-bill-template.options.lang.uk' },
        { id: 'eng', name: 'edit-bill-template.options.lang.eng' },
      ],
      currencyOptions: [
        { id: 'uah', name: 'edit-bill-template.options.currency.uah' },
      ],
      numberMask,
      billTemplateSaving: false,
    }
  },
  computed: {
    isTemplateNameValid() {
      return this.templateData.templateName.trim().length > 0
    },

    isPurposeValid() {
      return this.templateData.purpose.trim().length > 0
    },

    backLinkRoute() {
      return RouteHelper.getRoute('bills-templates', { companyId: this.id })
    },

    isFormValid() {
      return (
        this.isTemplateNameValid &&
        // this.isCurrencyValid &&
        // this.isSumValid &&
        this.isPurposeValid
        // this.isExpAtValid &&
        // this.isCommentValid
      )
    },
  },
  created() {
    this.loadBillTemplate()
  },
  methods: {
    async loadBillTemplate() {
      let _billTemplateResponse = await CompaniesApi.getBillTemplate(
        this.id,
        this.templateId
      )
      let _template = _billTemplateResponse.data.BillTemplate

      this.templateData = {
        sum: parseFloat(_template.Sum) / 100,
        purpose: _template.Purpose || '',
        currency: 'uah',
        templateName: _template.TemplateName,
        expAt: moment(_template.ExpiredAt).toDate(),
        comment: '',
      }
    },
    disabledDateHandler(val1) {
      return moment().diff(moment(val1), 'days') > 0
    },
    async saveBillTemplate() {
      if (this.billTemplateSaving) {
        return false
      }

      this.billTemplateSaving = true

      try {
        let _saveBillTemplateResponse = await CompaniesApi.updateBillTemplate(
          this.id,
          this.templateId,
          this.templateData
        )
        await this.$refs.billTemplateSavedNotification.show(3)
        await this.$router.push(
          RouteHelper.getRoute('bills-templates', { companyId: this.id })
        )
      } catch (e) {
        //TODO: Show error toast with error info
        return
      } finally {
        this.billTemplateSaving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.page {
  &--edit-bill-template {
    padding-top: 28px;
  }

  &__header {
    margin-bottom: 24px;
  }
}
.edit-bill-template {
  &__form {
    background: #ffffff;
    border-radius: 22px;
    padding: 45px 50px 74px;
    width: 100%;
    max-width: 390px;

    &-fields {
      margin-bottom: 19px;
    }

    &-field {
      margin-bottom: 25px;

      &--row {
        display: flex;
        align-items: stretch;
      }

      &-small {
        width: 95px;
        margin-right: 26px;
        flex-shrink: 0;
        flex-grow: 0;
      }

      &-big {
        flex-shrink: 1;
        flex-grow: 1;
      }

      &:last-child {
        margin-bottom: 0;
      }
    }

    &-template {
      margin-bottom: 38px;

      &-name {
        margin-top: 30px;
      }
    }

    &-action {
      padding: 25px 25px;
    }
  }
}
</style>
